import { LoadingButton } from '@mui/lab';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Transition } from '../../components/Transition';
import { NumericFormatCustom } from '../../components/NumericCustomFormatter';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import { useState } from 'react';
import { useRemountFileInput } from '../../hooks/useRemountFileInput';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DocumentType } from '../../enum';
import { uploadAssets } from '../../utils/uploadAssets';
import { getAssetURl } from '../../utils/url';
import { useAppSelector } from '../../store';

interface IProps {
  toggle: boolean;
  onClose: () => void;
  values: any;
  handleSubmit: () => void;
  setFieldValue: (key: string, value: any) => void;
  handleChange: (e: React.ChangeEvent<any>) => void;
  errors: any;
  touched: any;
  isSubmitting: boolean;
  resetForm: () => void;
}

const CategoryFormModal: React.FC<IProps> = ({
  toggle,
  onClose,
  errors,
  touched,
  values,
  isSubmitting,
  setFieldValue,
  handleChange,
  handleSubmit,
  resetForm,
}) => {
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));
  const [isPickerOpen, setIsPickerOpen] = useState<boolean>(false);
  const { fileInputRef, fileInputKey } = useRemountFileInput(
    toggle,
    values?.id,
    Boolean(values?.imgUrl),
    'imgUrl'
  );
  const { accessToken } = useAppSelector((state) => state.auth);
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );

  const toggleEmojiPicker = () => {
    setIsPickerOpen((prev) => !prev);
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    setFieldValue('emoji', emojiData?.emoji);
    setIsPickerOpen((prev) => !prev);
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    documentType: string,
    field: string
  ) => {
    if (event.target.files?.[0]) {
      const uploadResult: any = await uploadAssets(
        event.target.files?.[0],
        documentType,
        accessToken
      );
      if (uploadResult?.data?.id) {
        setFieldValue(field, uploadResult?.data?.keypath);
      }
    }
  };

  return (
    <Dialog
      fullWidth
      open={toggle}
      TransitionComponent={Transition}
      keepMounted
      onClose={() => {
        resetForm();
        setIsPickerOpen(false);
        onClose();
      }}
      aria-describedby='alert-dialog-slide-description'
    >
      <DialogTitle sx={{ mt: -0.5, mb: -0.5 }}>Occasion</DialogTitle>
      <form
        onSubmit={handleSubmit}
        autoComplete='off'
      >
        <DialogContent dividers>
          <Grid
            container
            spacing={2}
          >
            <Grid
              item
              xs={12}
              sm={12}
            >
              <TextField
                id='labelEn'
                name='labelEn'
                label='Label English'
                fullWidth
                variant='outlined'
                onChange={handleChange}
                value={values.labelEn}
                error={Boolean(errors.labelEn && touched.labelEn)}
                helperText={
                  Boolean(errors.labelEn && touched.labelEn) && errors.labelEn
                }
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={12}
            >
              <TextField
                id='labelAr'
                name='labelAr'
                label='Label Arabic'
                fullWidth
                variant='outlined'
                onChange={handleChange}
                value={values.labelAr}
                error={Boolean(errors.labelAr && touched.labelAr)}
                helperText={
                  Boolean(errors.labelAr && touched.labelAr) && errors.labelAr
                }
              />
            </Grid>
            <Grid
              item
              xs={values?.imgUrl ? 8.5 : 12}
              sm={values?.imgUrl ? 8.5 : 12}
            >
              <TextField
                key={fileInputKey}
                type='file'
                fullWidth
                name='imgUrl'
                id='imgUrl'
                inputRef={fileInputRef}
                onChange={(e) =>
                  handleFileChange(
                    e as React.ChangeEvent<HTMLInputElement>,
                    DocumentType.category,
                    'imgUrl'
                  )
                }
                inputProps={{ multiple: false, accept: 'image/*' }}
                InputLabelProps={{ shrink: true }}
                variant='outlined'
                label={'Image URL'}
                error={Boolean(errors.imgUrl && touched.imgUrl)}
                helperText={
                  Boolean(errors.imgUrl && touched.imgUrl) && errors.imgUrl
                }
              />
            </Grid>
            {values?.imgUrl && (
              <Grid
                item
                xs={3.5}
                sm={3.5}
              >
                <Avatar
                  imgProps={{ crossOrigin: 'anonymous' }}
                  src={getAssetURl(values?.imgUrl, assetUrl)}
                  sx={{
                    borderRadius: 'unset',
                    m: 1,
                    objectFit: 'contain',
                    '& img': { objectFit: 'contain' },
                    width: { xs: '75px', sm: '100px' },
                    height: 50,
                  }}
                />
              </Grid>
            )}
            {/* <Grid
              item
              xs={12}
              sm={12}
            >
              <TextField
                id='imgUrl'
                name='imgUrl'
                label={'Image URL'}
                fullWidth
                variant='outlined'
                onChange={handleChange}
                value={values.imgUrl}
                error={Boolean(errors.imgUrl && touched.imgUrl)}
                helperText={
                  Boolean(errors.imgUrl && touched.imgUrl) && errors.imgUrl
                }
              />
            </Grid> */}
            <Grid
              item
              xs={12}
              sm={12}
            >
              <TextField
                id='emoji'
                name='emoji'
                label={'Emoji'}
                fullWidth
                variant='outlined'
                onChange={handleChange}
                value={values.emoji}
                error={Boolean(errors.emoji && touched.emoji)}
                helperText={
                  Boolean(errors.emoji && touched.emoji) && errors.emoji
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton onClick={toggleEmojiPicker}>
                        <InsertEmoticonIcon />
                      </IconButton>
                      {isPickerOpen && (
                        <Box
                          position='absolute'
                          zIndex={1}
                          right={'50px'}
                          top={'-275%'}
                          style={{ width: isXsScreen ? '200px' : 'auto' }}
                        >
                          <EmojiPicker
                            onEmojiClick={handleEmojiClick}
                            skinTonesDisabled={true}
                            style={{
                              width: isXsScreen ? '200px' : 'auto',
                              height: '350px',
                            }}
                          />
                        </Box>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={12}
            >
              <TextField
                id='descriptionEn'
                name='descriptionEn'
                label={'Description English'}
                fullWidth
                variant='outlined'
                onChange={handleChange}
                value={values.descriptionEn}
                error={Boolean(errors.descriptionEn && touched.descriptionEn)}
                helperText={
                  Boolean(errors.descriptionEn && touched.descriptionEn) &&
                  errors.descriptionEn
                }
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={12}
            >
              <TextField
                id='descriptionAr'
                name='descriptionAr'
                label={'Description Arabic'}
                fullWidth
                variant='outlined'
                onChange={handleChange}
                value={values.descriptionAr}
                error={Boolean(errors.descriptionAr && touched.descriptionAr)}
                helperText={
                  Boolean(errors.descriptionAr && touched.descriptionAr) &&
                  errors.descriptionAr
                }
              />
            </Grid>

            {/* <Grid
              item
              xs={12}
              sm={12}
            >
              <TextField
                id='itemsSortType'
                name='itemsSortType'
                label={'Sort type'}
                fullWidth
                variant='outlined'
                onChange={handleChange}
                value={values.itemsSortType}
                error={Boolean(errors.itemsSortType && touched.itemsSortType)}
                helperText={
                  Boolean(errors.itemsSortType && touched.itemsSortType) &&
                  errors.itemsSortType
                }
              />
            </Grid> */}
            <Grid
              item
              xs={12}
              sm={12}
            >
              <TextField
                id='position'
                name='position'
                label={'Position'}
                fullWidth
                variant='outlined'
                onChange={handleChange}
                value={values.position}
                InputProps={{
                  inputComponent: NumericFormatCustom as any,
                }}
                error={Boolean(errors.position && touched.position)}
                helperText={
                  Boolean(errors.position && touched.position) &&
                  errors.position
                }
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    color='primary'
                    name='activeState'
                    onChange={(e) =>
                      setFieldValue('activeState', e.target.checked)
                    }
                    checked={values.activeState}
                  />
                }
                label={'Active Status'}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    color='primary'
                    name='isPrimary'
                    onChange={(e) =>
                      setFieldValue('isPrimary', e.target.checked)
                    }
                    checked={values.isPrimary}
                  />
                }
                label={'Primary'}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              resetForm();
              setIsPickerOpen(false);
              onClose();
            }}
            variant='outlined'
          >
            {'Cancel'}
          </Button>
          <LoadingButton
            type='submit'
            loading={isSubmitting}
            variant='contained'
          >
            {'Save'}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CategoryFormModal;
